import { axios } from '~/modules/axios';
import { AI_API_URL } from '~/lib/environment';
import { aiApi } from '~/modules/query/ai';
import type {
  AnalyticsConversationApiResponse,
  AnalyticsFilterOptionsApiResponse,
  AnalyticsFilterOptionsQueryArgs,
  AnalyticsOverviewApiResponse,
  AnalyticsOverviewQueryArgs,
  AnalyticsTelemetryApiResponse,
  AnalyticsTelemetryExportQueryArgs,
  AnalyticsTelemetryExportResult,
  AnalyticsTelemetryQueryArgs,
} from '~/modules/query/ai/types';

const seg = encodeURIComponent;

// `/telemetry` and `/telemetry/export` share the same filters, minus pagination.
function buildTelemetryParams(
  args: AnalyticsTelemetryQueryArgs | AnalyticsTelemetryExportQueryArgs,
): URLSearchParams {
  const params = new URLSearchParams();
  params.set('source', args.source);
  if (args.userId) params.set('userId', args.userId);
  if (args.entityKey) params.set('entityKey', args.entityKey);
  if (args.model) params.set('model', args.model);
  if (args.status) params.set('status', args.status);
  if (args.startDate) params.set('startDate', args.startDate);
  if (args.endDate) params.set('endDate', args.endDate);
  if (args.search) params.set('search', args.search);
  if (args.sort) params.set('sort', args.sort);
  if (args.sortDirection) params.set('sortDirection', args.sortDirection);
  if ('page' in args && args.page) params.set('page', String(args.page));
  if ('pageSize' in args && args.pageSize) params.set('pageSize', String(args.pageSize));
  return params;
}

// The export is a CSV body plus a Content-Disposition filename, so it cannot go
// through `axiosBaseQuery` (which keeps only `response.data`). The shared axios
// instance still owns the Bearer header and the 401 redirect.
const exportTelemetry = async (
  args: AnalyticsTelemetryExportQueryArgs,
): Promise<
  | { data: AnalyticsTelemetryExportResult }
  | { error: { status?: number; data?: unknown; message: string } }
> => {
  try {
    const response = await axios({
      url: `${AI_API_URL}/manager/analytics/telemetry/export?${buildTelemetryParams(args).toString()}`,
      method: 'GET',
      responseType: 'text',
      timeout: 180000,
    });

    const disposition = String(response.headers['content-disposition'] ?? '');
    const filename = /filename="?([^";]+)"?/.exec(disposition)?.[1] ?? 'telemetry-export.csv';

    return { data: { filename, csvContent: String(response.data ?? '') } };
  } catch (error) {
    if (axios.isAxiosError(error)) {
      const rawBody = error.response?.data;
      let body: unknown = rawBody ?? { message: error.message };

      // responseType "text" leaves an error body as the raw string; parse it so
      // the page can read `{error, code}` like every other analytics failure.
      if (typeof rawBody === 'string') {
        try {
          body = JSON.parse(rawBody);
        } catch {
          body = { message: error.message };
        }
      }

      return {
        error: {
          status: error.response?.status,
          data: body,
          message: error.message,
        },
      };
    }

    return { error: { message: error instanceof Error ? error.message : 'Request failed' } };
  }
};

export const analyticsApi = aiApi.injectEndpoints({
  endpoints: (builder) => ({
    // The Overview page's token budget and 30-day trends come from the usage group
    // in `settings.ts` (`useGetUsageQuery` / `useGetUsageStatsQuery`); they are not
    // duplicated here.
    getAnalyticsOverview: builder.query<AnalyticsOverviewApiResponse, AnalyticsOverviewQueryArgs>({
      query: ({ source, startDate, endDate, placementKeys, assistantKeys, models }) => {
        const params = new URLSearchParams();
        params.set('source', source);
        if (startDate) params.set('startDate', startDate);
        if (endDate) params.set('endDate', endDate);
        for (const key of placementKeys ?? []) params.append('placementKeys', key);
        for (const key of assistantKeys ?? []) params.append('assistantKeys', key);
        for (const model of models ?? []) params.append('models', model);
        return { url: `/manager/analytics/overview?${params.toString()}`, method: 'GET' };
      },
      providesTags: ['AiAnalytics'],
    }),
    getAnalyticsFilterOptions: builder.query<
      AnalyticsFilterOptionsApiResponse,
      AnalyticsFilterOptionsQueryArgs
    >({
      query: ({ source }) => ({
        url: `/manager/analytics/filter-options?source=${seg(source)}`,
        method: 'GET',
      }),
      providesTags: ['AiAnalytics'],
    }),
    getAnalyticsTelemetry: builder.query<
      AnalyticsTelemetryApiResponse,
      AnalyticsTelemetryQueryArgs
    >({
      query: (args) => ({
        url: `/manager/analytics/telemetry?${buildTelemetryParams(args).toString()}`,
        method: 'GET',
      }),
      providesTags: ['AiAnalytics'],
    }),
    getAnalyticsConversation: builder.query<AnalyticsConversationApiResponse, string>({
      query: (sessionId) => ({
        url: `/manager/analytics/telemetry/${seg(sessionId)}/conversation`,
        method: 'GET',
      }),
      providesTags: (_result, _error, sessionId) => [{ type: 'AiAnalytics', id: sessionId }],
    }),
    exportAnalyticsTelemetry: builder.query<
      AnalyticsTelemetryExportResult,
      AnalyticsTelemetryExportQueryArgs
    >({
      queryFn: exportTelemetry,
    }),
  }),
});

export const {
  useGetAnalyticsOverviewQuery,
  useGetAnalyticsFilterOptionsQuery,
  useGetAnalyticsTelemetryQuery,
  useGetAnalyticsConversationQuery,
  useLazyExportAnalyticsTelemetryQuery,
} = analyticsApi;
